import React, { useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * EquipmentOverridePattern - Replace standard weapons with campaign-specific equipment.
 *
 * Used by Motherboard (Ikonis + Augments).
 *
 * Config properties:
 * - displayName: equipment system name
 * - icon: emoji icon
 * - colorClass: Tailwind classes
 * - equipmentKey: character state key (e.g., 'ikonis')
 * - baseFeatureSource: path to base feature in frameStaticData
 * - augmentSlotsSource: path to augment slot table in frameStaticData
 * - augmentsSource: path to available augments list in frameStaticData
 */
const EquipmentOverridePattern = ({
  config,
  character,
  updateCharacter,
  frameStaticData,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('ikonis'); // ikonis | augments | craft
  const [selectedAugmentId, setSelectedAugmentId] = useState(null);

  // Resolve data
  const ikonisData = frameStaticData?.customMechanics?.ikonis;
  const augmentsList = frameStaticData?.customMechanics?.augments || [];
  const scrapData = frameStaticData?.customMechanics?.scrap;

  const equipmentKey = config.equipmentKey || 'ikonis';
  const ikonisState = character?.[equipmentKey] || {
    name: 'My Ikonis',
    description: '',
    craftedAugments: [],
    installedAugments: [],
  };

  // Calculate tier from level
  const tier = useMemo(() => {
    const level = character?.level || 1;
    if (level >= 7) return 4;
    if (level >= 5) return 3;
    if (level >= 3) return 2;
    return 1;
  }, [character?.level]);

  const maxSlots = ikonisData?.augmentSlots?.[`tier${tier}`] || 2;
  const installedCount = ikonisState.installedAugments?.length || 0;

  // ---------------------------------------------------------------------------
  // Augment actions
  // ---------------------------------------------------------------------------

  const handleInstallAugment = useCallback((augmentId) => {
    if (installedCount >= maxSlots) {
      toast.error(`All ${maxSlots} augment slots are full!`);
      return;
    }

    const crafted = ikonisState.craftedAugments || [];
    const augment = crafted.find(a => a.id === augmentId);
    if (!augment) {
      toast.error('Augment not found in crafted list');
      return;
    }

    if (ikonisState.installedAugments?.find(a => a.id === augmentId)) {
      toast.error('Already installed!');
      return;
    }

    const newState = {
      ...ikonisState,
      installedAugments: [...(ikonisState.installedAugments || []), augment],
    };

    updateCharacter({ ...character, [equipmentKey]: newState });
    toast.success(`Installed ${augment.name}!`);
  }, [ikonisState, installedCount, maxSlots, character, equipmentKey, updateCharacter]);

  const handleUninstallAugment = useCallback((augmentId) => {
    const newState = {
      ...ikonisState,
      installedAugments: (ikonisState.installedAugments || []).filter(a => a.id !== augmentId),
    };

    updateCharacter({ ...character, [equipmentKey]: newState });
    toast.success('Augment removed');
  }, [ikonisState, character, equipmentKey, updateCharacter]);

  const handleCraftAugment = useCallback((augment) => {
    // Check if already crafted
    if (ikonisState.craftedAugments?.find(a => a.id === augment.id)) {
      toast.error('Already crafted!');
      return;
    }

    // Check scrap cost
    const scrapInventory = character?.inventory?.scrap || {};
    const cost = augment.scrapCost || {};
    const missingItems = [];

    for (const [scrapName, needed] of Object.entries(cost)) {
      // Count matching scrap across all categories
      let available = 0;
      for (const categoryItems of Object.values(scrapInventory)) {
        available += (categoryItems || []).filter(
          item => item.name.toLowerCase() === scrapName.toLowerCase()
        ).length;
      }
      if (available < needed) {
        missingItems.push(`${scrapName} (need ${needed}, have ${available})`);
      }
    }

    if (missingItems.length > 0) {
      toast.error(`Missing scrap: ${missingItems.join(', ')}`);
      return;
    }

    // Deduct scrap (simplified — removes from first matching category)
    const newScrapInventory = { ...scrapInventory };
    for (const [scrapName, needed] of Object.entries(cost)) {
      let remaining = needed;
      for (const catKey of Object.keys(newScrapInventory)) {
        if (remaining <= 0) break;
        const items = [...(newScrapInventory[catKey] || [])];
        const newItems = [];
        for (const item of items) {
          if (remaining > 0 && item.name.toLowerCase() === scrapName.toLowerCase()) {
            remaining--;
          } else {
            newItems.push(item);
          }
        }
        newScrapInventory[catKey] = newItems;
      }
    }

    const newState = {
      ...ikonisState,
      craftedAugments: [...(ikonisState.craftedAugments || []), { ...augment, craftedAt: new Date().toISOString() }],
    };

    updateCharacter({
      ...character,
      [equipmentKey]: newState,
      inventory: { ...character.inventory, scrap: newScrapInventory },
    });

    toast.success(`Crafted ${augment.name}!`);
  }, [ikonisState, character, equipmentKey, updateCharacter]);

  const handleUpdateIkonisName = useCallback((name) => {
    updateCharacter({
      ...character,
      [equipmentKey]: { ...ikonisState, name },
    });
  }, [ikonisState, character, equipmentKey, updateCharacter]);

  const handleUpdateIkonisDescription = useCallback((description) => {
    updateCharacter({
      ...character,
      [equipmentKey]: { ...ikonisState, description },
    });
  }, [ikonisState, character, equipmentKey, updateCharacter]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-purple-400 mb-1">
          {config.icon || '⚔️'} {config.displayName || 'Equipment'}
        </h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {['ikonis', 'augments', 'craft'].map((tab) => (
          <DomainCardButton
            key={tab}
            onClick={() => setActiveTab(tab)}
            variant={activeTab === tab ? 'active' : 'secondary'}
            className="flex-1 capitalize"
          >
            {tab}
          </DomainCardButton>
        ))}
      </div>

      {/* Ikonis Tab */}
      {activeTab === 'ikonis' && (
        <div className="space-y-3">
          {/* Ikonis name/description */}
          <div className="space-y-2">
            <input
              type="text"
              value={ikonisState.name || ''}
              onChange={(e) => handleUpdateIkonisName(e.target.value)}
              placeholder="Name your Ikonis..."
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-200"
            />
            <textarea
              value={ikonisState.description || ''}
              onChange={(e) => handleUpdateIkonisDescription(e.target.value)}
              placeholder="Describe your Ikonis (staff, bow, hammer, spear...)"
              rows={2}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-200"
            />
          </div>

          {/* Base feature */}
          {ikonisData?.baseFeature && (
            <div className="bg-purple-900/20 p-3 rounded-lg border border-purple-700">
              <p className="font-medium text-purple-300">
                {ikonisData.baseFeature.name}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {ikonisData.baseFeature.description}
              </p>
              <p className="text-xs text-purple-400 mt-1">
                Current bonus: +{character?.level || 1} to damage rolls
              </p>
            </div>
          )}

          {/* Properties */}
          <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-400">Type:</div>
              <div className="text-gray-200">Two-Handed</div>
              <div className="text-gray-400">Tier:</div>
              <div className="text-gray-200">{tier}</div>
              <div className="text-gray-400">Augment Slots:</div>
              <div className="text-gray-200">{installedCount} / {maxSlots}</div>
            </div>
          </div>

          {/* Installed Augments */}
          {(ikonisState.installedAugments || []).length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-300">Installed Augments</p>
              {ikonisState.installedAugments.map((aug) => (
                <div key={aug.id} className="flex items-start justify-between bg-purple-900/20 p-2 rounded border border-purple-700">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-purple-300">{aug.name}</p>
                    <p className="text-xs text-gray-400">{aug.effect}</p>
                  </div>
                  <DomainCardButton
                    onClick={() => handleUninstallAugment(aug.id)}
                    variant="secondary"
                    className="px-2 py-1 ml-2"
                  >
                    Remove
                  </DomainCardButton>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Augments Tab (install/swap) */}
      {activeTab === 'augments' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-400">
            Slots: {installedCount}/{maxSlots} (Tier {tier}). Swap augments during downtime.
          </p>

          {(ikonisState.craftedAugments || []).length === 0 ? (
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-600 text-center">
              <p className="text-sm text-gray-400">No augments crafted yet. Go to the Craft tab.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(ikonisState.craftedAugments || []).map((aug) => {
                const isInstalled = (ikonisState.installedAugments || []).find(a => a.id === aug.id);
                return (
                  <div key={aug.id} className={`p-3 rounded-lg border ${isInstalled ? 'bg-purple-900/30 border-purple-600' : 'bg-gray-800/50 border-gray-600'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-200">
                          {aug.name} {isInstalled && <span className="text-purple-400 text-xs">(installed)</span>}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{aug.effect}</p>
                      </div>
                      <DomainCardButton
                        onClick={() => isInstalled ? handleUninstallAugment(aug.id) : handleInstallAugment(aug.id)}
                        variant={isInstalled ? 'secondary' : 'primary'}
                        className="px-2 py-1 ml-2"
                        disabled={!isInstalled && installedCount >= maxSlots}
                      >
                        {isInstalled ? 'Remove' : 'Install'}
                      </DomainCardButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Craft Tab */}
      {activeTab === 'craft' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-400">
            Spend scrap to craft augments during downtime.
          </p>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {augmentsList.map((aug) => {
              const alreadyCrafted = (ikonisState.craftedAugments || []).find(a => a.id === aug.id);
              const isSelected = selectedAugmentId === aug.id;

              return (
                <div
                  key={aug.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    alreadyCrafted
                      ? 'bg-green-900/20 border-green-700'
                      : isSelected
                        ? 'bg-purple-900/30 border-purple-600'
                        : 'bg-gray-800/50 border-gray-600 hover:bg-gray-700/50'
                  }`}
                  onClick={() => !alreadyCrafted && setSelectedAugmentId(isSelected ? null : aug.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-200">
                        {aug.name}
                        {alreadyCrafted && <span className="text-green-400 text-xs ml-2">Crafted</span>}
                      </p>
                      <p className="text-xs text-gray-500">Precompile: {aug.precompile}</p>
                      <p className="text-xs text-gray-400 mt-1">{aug.effect}</p>
                    </div>
                  </div>

                  {/* Scrap cost (expanded) */}
                  {isSelected && aug.scrapCost && (
                    <div className="mt-2 pt-2 border-t border-gray-600">
                      <p className="text-xs text-gray-400 mb-1">Scrap Cost:</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(aug.scrapCost).map(([name, qty]) => (
                          <span key={name} className="px-2 py-0.5 text-xs bg-gray-700 rounded text-gray-300">
                            {qty}x {name}
                          </span>
                        ))}
                      </div>
                      <DomainCardButton
                        onClick={(e) => { e.stopPropagation(); handleCraftAugment(aug); }}
                        variant="activeSuccess"
                        className="w-full mt-2"
                      >
                        Craft {aug.name}
                      </DomainCardButton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {onClose && (
        <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
          Close
        </DomainCardButton>
      )}
    </div>
  );
};

export default EquipmentOverridePattern;
